import { useEffect } from "react";

import {
  __ME_STEP_CURRENT_WORKFLOW__,
  IWebhook,
  META_EXPRESSION_SCOPE,
  TRIGGER_EVENT,
} from "fieldpro-tools";
import _ from "lodash";
import { useSelector } from "react-redux";

import { getSelectedClient } from "containers/clients/redux/selectors";
import { getPersonaOptions } from "containers/users/subcategories/utils";
import { IOption } from "model/application/components";
import { TInputAttributeLang } from "model/application/Lang";
import { TExpressionType } from "model/entities/Expression";
import { ITransformation } from "model/entities/Job";
import { IList } from "model/entities/List";
import { CUSTOM_FIELD_TYPE, IActivity } from "model/entities/Workflow";
import {
  isFieldWithOptions,
  isMxolField,
  isPictureField,
  isScolOrMcolField,
} from "utils/metaExpressions/fieldTypes";
import { getCustomFieldAttributesInSchema } from "utils/metaExpressions/getCustomFieldAttributesInSchema";
import { getCustomFieldOptions } from "utils/metaExpressions/getCustomFieldOptions";
import getFieldTagOptions from "utils/metaExpressions/getFieldTagOptions";
import { getSchemaForMetaExpression } from "utils/metaExpressions/getSchemaForMetaExpression";

import InputBoolean from "../../../Input/InputBoolean";
import { getMobileUserFieldOptions } from "../../../Input/InputMetaExpression/utils/getMobileUserFieldOptions";
import WorkflowReportsFieldSelection from "../../../Input/InputMetaExpression/WorkflowReportsFieldSelection";
import InputSelect from "../../../Input/InputSelect";
import { CALLED_FROM } from "./MetaExpressionWizard";

/**
 * Third step : field & custom fields selections
 */
export interface IMetaExpressionWizardThirdStepProps {
  // the properties to change
  calledFrom: CALLED_FROM | undefined;
  scope?: META_EXPRESSION_SCOPE;
  subscope?: META_EXPRESSION_SCOPE;
  custom_field1?: string;
  custom_field2?: string;
  custom_field3?: string;
  option_tag?: string;
  field?: string;
  // the additionnal props
  lists?: IList[]; // => all available lists
  activities?: IActivity[]; // => all available activities
  webhooks?: IWebhook[];
  transformations?: ITransformation[]; // => all available transformations
  currentResourceId?: string; // => the current resource id selected (ex: activity id)
  numberIndex?: number;
  mobileUserRoleOptions: IOption[];
  // the usual properties generically passed in the step
  expressionType?: TExpressionType;
  calledFromMatrixTag?: string;
  shouldMarkError: any;
  labelToDisplay: any;
  useDefaultFields: boolean;
  useSecondDefaultFields?: boolean;
  isSecondLevel?: boolean;
  onChange: (value: any, name: string) => void;
  type?: CUSTOM_FIELD_TYPE;
  triggerEvent?: TRIGGER_EVENT;
  step?: string;
}

export const MetaExpressionWizardThirdStep: React.FunctionComponent<
  IMetaExpressionWizardThirdStepProps
> = ({
  scope,
  subscope,
  custom_field1,
  custom_field2,
  custom_field3,
  option_tag,
  field,
  lists = [],
  activities = [],
  webhooks = [],
  transformations = [],
  currentResourceId = "",
  numberIndex,
  mobileUserRoleOptions,
  expressionType,
  calledFrom,
  calledFromMatrixTag,
  shouldMarkError,
  labelToDisplay,
  useDefaultFields,
  useSecondDefaultFields,
  isSecondLevel,
  onChange,
  type,
  triggerEvent,
  step,
}) => {
  const onChangeAdditional = (value: any, name: string): void => {
    if (isSecondLevel) {
      if (name === "custom_field1") {
        onChange(value, "custom_field2");
        return;
      }
      if (name === "custom_field2") {
        onChange(value, "custom_field3");
        return;
      }
    }
    onChange(value, name);
  };

  const DETECT_RESULT_FIELD = "detect_result";

  // Derive mainSchema early so it is available for the useEffect below.
  const mainSchemaEarly = getSchemaForMetaExpression({
    activities,
    lists,
    transformations,
    webhooks,
    params: {
      scope,
      currentResourceId,
      currentSubResourceId: calledFromMatrixTag,
      numberIndex,
      type,
      includeMatrix: true,
      triggerEvent,
    },
  });

  const selectedCustomField1 = custom_field1
    ? getCustomFieldAttributesInSchema(custom_field1, mainSchemaEarly)
    : undefined;

  // When a PICTURE custom field is selected, auto-set field = detect_result.
  useEffect(() => {
    if (
      selectedCustomField1 &&
      isPictureField(selectedCustomField1.type) &&
      field !== DETECT_RESULT_FIELD
    ) {
      onChange(DETECT_RESULT_FIELD, "field");
    }
  }, [field, onChange, selectedCustomField1]);

  const currentClient = useSelector(getSelectedClient);

  if (
    scope === META_EXPRESSION_SCOPE.WORKFLOW_REPORT &&
    step === __ME_STEP_CURRENT_WORKFLOW__
  ) {
    return <WorkflowReportsFieldSelection onChange={onChange} value={field} />;
  }

  // For CLIENT_ENV_VARIABLE scope, show client secrets as field options
  if (scope === META_EXPRESSION_SCOPE.CLIENT_ENV_VARIABLE) {
    const envVarOptions: IOption[] = (currentClient?.secrets ?? []).map(
      (s) => ({
        key: s.name,
        label: s.name,
      }),
    );

    return (
      <div>
        <InputSelect
          viewMode="CREATE"
          error={shouldMarkError("field")}
          name="field"
          lang={labelToDisplay.inputOption}
          options={_.sortBy(envVarOptions, "label")}
          value={field}
          onChange={onChange}
          dataTestId="env-variable-field-options"
        />
      </div>
    );
  }

  let bannerMessage = "";
  let metaOrCustom = <></>;
  let mainSelect: JSX.Element;
  let secondSelect = <></>;

  // build the banner message
  if (
    scope === META_EXPRESSION_SCOPE.ACTIVITY_REPORT ||
    scope === META_EXPRESSION_SCOPE.WORKFLOW_REPORT
  ) {
    bannerMessage = `*The current activity report is automatically selected`;
  } else if (
    scope === META_EXPRESSION_SCOPE.ITEM ||
    scope === META_EXPRESSION_SCOPE.ITEM_CELL
  ) {
    bannerMessage = `*The current list is automatically selected`;
  }
  const mainSchema = mainSchemaEarly;

  const isItemsRestrictionsContext =
    scope === META_EXPRESSION_SCOPE.ITEM &&
    (calledFrom === CALLED_FROM.ACTIVITY_ITEMS_RESTRICTIONS ||
      calledFrom === CALLED_FROM.LIST);

  const customFieldOptions: IOption[] = getCustomFieldOptions(mainSchema, {
    scope,
    expressionType,
    includePictureType: isItemsRestrictionsContext,
  });

  let metaFieldOptions: IOption[] = getFieldTagOptions(
    scope,
    subscope,
    mobileUserRoleOptions,
  );

  // Keep only the _customer_id option
  if (calledFrom === CALLED_FROM.CUSTOMER_TO_USE) {
    metaFieldOptions = _.filter(metaFieldOptions, { key: "_customer_id" });
  }

  if (calledFrom === CALLED_FROM.CUSTOMER_TO_USE) {
    useDefaultFields = true;
  } else if (customFieldOptions.length > 0 && metaFieldOptions.length > 0) {
    // if we have both custom field options and field options, we should add a switch
    const langTitle = {
      title: "Meta expression based on activity report normal field",
    } as TInputAttributeLang; // TODO: put this one on Lokalise

    metaOrCustom = (
      <InputBoolean
        name={isSecondLevel ? "useSecondDefaultFields" : "useDefaultFields"}
        lang={langTitle}
        defaultValue={useDefaultFields}
        onChange={onChange}
      />
    );
  } else if (metaFieldOptions.length == 0) {
    // we have to display the custom field only
    useDefaultFields = false;
  } else if (customFieldOptions.length == 0) {
    // we have to display the meta field only
    useDefaultFields = true;
  }

  if (useDefaultFields) {
    // display the meta fields
    mainSelect = (
      <InputSelect
        viewMode="CREATE"
        error={shouldMarkError("field")}
        name="field"
        lang={labelToDisplay.inputOption}
        options={_.sortBy(metaFieldOptions, "label")}
        value={field}
        onChange={onChange}
        dataTestId="field-options"
      />
    );

    if (field === "_role" && scope === META_EXPRESSION_SCOPE.USER) {
      secondSelect = (
        <InputSelect
          viewMode="CREATE"
          error={shouldMarkError("option_tag")}
          name="option_tag"
          lang={labelToDisplay.inputOption}
          options={mobileUserRoleOptions}
          value={option_tag}
          onChange={onChange}
        />
      );
    }
    if (field === "_persona" && scope === META_EXPRESSION_SCOPE.USER) {
      secondSelect = (
        <InputSelect
          viewMode="CREATE"
          error={shouldMarkError("option_tag")}
          name="option_tag"
          lang={labelToDisplay.inputOption}
          options={getPersonaOptions()}
          value={option_tag}
          onChange={onChange}
        />
      );
    }

    if (field === "_team_leader") {
      secondSelect = (
        <InputSelect
          viewMode="CREATE"
          error={shouldMarkError("option_tag")}
          name="option_tag"
          lang={labelToDisplay.inputOption}
          options={getMobileUserFieldOptions()}
          value={option_tag}
          onChange={onChange}
        />
      );
    }
  } else {
    // display the custom fields
    mainSelect = (
      <InputSelect
        viewMode="CREATE"
        error={shouldMarkError(
          isSecondLevel ? "custom_field2" : "custom_field1",
        )}
        name={isSecondLevel ? "custom_field2" : "custom_field1"}
        lang={labelToDisplay.inputField}
        options={customFieldOptions}
        value={custom_field1}
        onChange={onChange}
      />
    );

    const customField1 = selectedCustomField1;

    // 1st case: "options" field
    if (customField1 && isFieldWithOptions(customField1.type)) {
      // NOTE: see FP-3746
      // Since SC and MC options represent strings, it would be better to create a MetaExpression on the SC or MC **field**
      // (not option) and then allow the user to select the option after the operator in conditions and operations.
      const fieldOptions = customField1?.options;
      secondSelect = (
        <InputSelect
          viewMode="CREATE"
          error={shouldMarkError("option_tag")}
          name={"option_tag"}
          lang={labelToDisplay.inputOption}
          options={fieldOptions || []}
          value={option_tag}
          onChange={onChange}
          clearable
        />
      );
    }

    // 2nd case: picture field (detect result) - field is auto-set via useEffect
    if (customField1 && isPictureField(customField1.type)) {
      secondSelect = (
        <InputSelect
          viewMode="CREATE"
          error={false}
          name="field"
          lang={{ title: "Detect result" } as any}
          options={[{ key: DETECT_RESULT_FIELD, label: "Detect result" }]}
          value={DETECT_RESULT_FIELD}
          onChange={() => undefined}
          disabled
        />
      );
    }

    // 4th case: matrix on list field
    if (customField1 && isMxolField(customField1.type)) {
      const matrixSchema = getSchemaForMetaExpression({
        activities,
        lists,
        transformations,
        webhooks,
        params: {
          scope,
          currentResourceId,
          currentSubResourceId: custom_field1, // the matrix tag
          type,
          includeMatrix: false, // we want to include the matrix here
          triggerEvent,
          isSubCustomField: true,
        },
      });
      secondSelect = (
        <InputSelect
          viewMode="CREATE"
          error={shouldMarkError("custom_field2")}
          name={"custom_field2"}
          lang={labelToDisplay.inputField}
          options={getCustomFieldOptions(matrixSchema, {
            scope: META_EXPRESSION_SCOPE.ACTIVITY_REPORT,
            expressionType,
          })}
          value={custom_field2}
          onChange={onChange}
          clearable
        />
      );
    }

    // 5th case: SCOL or MCOL => we can select a nested field, recursively call the component
    if (customField1 && isScolOrMcolField(customField1.type)) {
      secondSelect = (
        <MetaExpressionWizardThirdStep
          scope={META_EXPRESSION_SCOPE.ITEM}
          calledFrom={CALLED_FROM.MATRIX_CELL}
          isSecondLevel={true}
          useDefaultFields={useSecondDefaultFields ? true : false}
          currentResourceId={customField1.list_id}
          activities={activities}
          lists={lists}
          mobileUserRoleOptions={[]} // not used here
          expressionType={expressionType}
          custom_field1={custom_field2} // this one is important!!
          custom_field2={custom_field3}
          option_tag={option_tag}
          field={field}
          shouldMarkError={shouldMarkError}
          labelToDisplay={labelToDisplay}
          onChange={onChangeAdditional}
        />
      );
    }
  }

  return (
    <div>
      <>{bannerMessage}</>
      <>{metaOrCustom}</>
      <>{mainSelect}</>
      <>{secondSelect}</>
    </div>
  );
};
